import React, { useEffect, useState } from 'react';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Tag from '~addListing/pages/DetailsStep/Tag';
import Toggle from '~/components/Toggle';

const notRegisterId = 4;

const PropertyRegistrationStatus = ({
  customFieldsData,
  listing,
}: {
  listing: ListingDetailsType;
  customFieldsData: {
    propertyRegistrationStatus: { value: string; key: number }[];
  };
}) => {
  const { t } = useTranslation('app');
  const isForSale = listing?.section.slug === 'for-sale';
  const eligibleForMortgage = listing?.location?.eligible_for_mortgage;
  const [selectedTag, setSelectedTag] = useState<number | null>(null);
  const [isMortgageApproved, setIsMortgageApproved] = useState(false);
  const [isError, setIsError] = useState(false);
  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<addListingDetailsType>();

  const handleSelectPropertyRegistrationStatus = (key: number) => {
    setSelectedTag(key);
    setValue('propertyRegistrationStatus', key);
    clearErrors('propertyRegistrationStatus');
  };
  const isReadyToMortgage =
    watch('propertyRegistrationStatus') !== notRegisterId;

  if (!isForSale || !eligibleForMortgage) return null;

  useEffect(() => {
    if (!isReadyToMortgage) setValue('isMortgageApproved', 0);
  }, [watch('propertyRegistrationStatus')]);

  useEffect(() => {
    if (listing?.propertyRegistrationStatus) {
      setSelectedTag(listing.propertyRegistrationStatus);
      setValue(
        'propertyRegistrationStatus',
        listing.propertyRegistrationStatus
          ? listing.propertyRegistrationStatus
          : 0,
      );
    }
    if (listing?.isMortgageApproved) {
      setIsMortgageApproved(listing.isMortgageApproved);
      setValue('isMortgageApproved', listing.isMortgageApproved ? 1 : 0);
    }
  }, [listing]);

  useEffect(() => {
    if (
      !watch('propertyRegistrationStatus') &&
      watch('isPropertyRegistrationStatusVisible') &&
      Object.keys(errors).length > 0
    )
      setIsError(true);
    else setIsError(false);
  }, [errors, watch('propertyRegistrationStatus')]);

  useEffect(() => {
    if (isForSale && eligibleForMortgage)
      setValue('isPropertyRegistrationStatusVisible', true);
    else setValue('isPropertyRegistrationStatusVisible', false);
  }, []);

  return (
    <div className="flex flex-col gap-2x">
      <p className="text-title_4 text-black">
        {t('propertyRegistrationStatus')}
      </p>
      <div className="flex flex-wrap gap-1.5x">
        {customFieldsData.propertyRegistrationStatus.map((tag) => (
          <Tag
            key={tag.key}
            text={tag.value}
            isSelected={selectedTag === tag.key}
            onClick={() => handleSelectPropertyRegistrationStatus(tag.key)}
            className={
              errors.propertyRegistrationStatus || isError
                ? '!border-red_dark'
                : '!border-gray'
            }
          />
        ))}
      </div>
      {isReadyToMortgage && (
        <div className="flex items-center gap-2x">
          <p className="text-gray__dark_2 font-[700] text-[14] leading-[24px]">
            {t('propertyIsMortgageApproved')}
          </p>
          <Toggle
            open={isMortgageApproved}
            onChange={(e) => {
              setIsMortgageApproved(e.currentTarget.checked);
              setValue('isMortgageApproved', e.currentTarget.checked ? 1 : 0);
            }}
          />
        </div>
      )}
      {(errors['propertyRegistrationStatus'] || isError) && (
        <p className="text-red_dark">{t('propertyRegistrationStatusError')}</p>
      )}
    </div>
  );
};

export default PropertyRegistrationStatus;
